import React, { useState } from 'react';
import { Trash2, Edit2, Share2, Languages } from 'lucide-react';
import ShareQuote from './ShareQuote';
import { translateText, getDeviceLanguage } from '../utils/translationUtils';

type FavoriteQuote = {
  id: number;
  text: string;
  author: string;
  date: string;
  collectedAt: number;
  insight?: string;
};

type FavoritesProps = {
  favorites: FavoriteQuote[];
  onRemove: (id: number) => void;
  onUpdateInsight: (id: number, insight: string) => void;
};

const Favorites: React.FC<FavoritesProps> = ({ favorites, onRemove, onUpdateInsight }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingInsight, setEditingInsight] = useState('');
  const [showShareModal, setShowShareModal] = useState<number | null>(null);
  const [isTranslated, setIsTranslated] = useState<{ [key: number]: boolean }>({});
  const [translatedTexts, setTranslatedTexts] = useState<{ [key: number]: string }>({});
  const [showTranslationError, setShowTranslationError] = useState(false);

  const sortedFavorites = [...favorites].sort((a, b) => b.collectedAt - a.collectedAt);

  const handleEditClick = (id: number, insight: string = '') => {
    setEditingId(id);
    setEditingInsight(insight);
  };

  const handleSaveInsight = (id: number) => {
    onUpdateInsight(id, editingInsight);
    setEditingId(null);
  };

  const handleTranslate = async (quote: FavoriteQuote) => {
    if (!isTranslated[quote.id]) {
      try {
        const deviceLanguage = getDeviceLanguage();
        const translated = await translateText(quote.text, deviceLanguage);
        if (translated) {
          setTranslatedTexts(prev => ({ ...prev, [quote.id]: translated }));
          setIsTranslated(prev => ({ ...prev, [quote.id]: true }));
        } else {
          throw new Error('Translation failed');
        }
      } catch (error) {
        console.error('Translation error:', error);
        setShowTranslationError(true);
        setTimeout(() => setShowTranslationError(false), 3000);
      }
    } else {
      setIsTranslated(prev => ({ ...prev, [quote.id]: false }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-inner p-4 relative">
      {showTranslationError && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-2 px-4 rounded-t-lg">
          翻译功能当前网络条件下不可用
        </div>
      )}
      {sortedFavorites.length === 0 ? (
        <p className="text-center text-gray-500">No favorites yet. Add some quotes to your favorites!</p>
      ) : (
        <ul className="space-y-6">
          {sortedFavorites.map((quote) => (
            <li key={`favorite-${quote.id}-${quote.collectedAt}`} className="border-b pb-4 last:border-b-0">
              <blockquote className="text-xl italic mb-2 text-gray-700">
                "{isTranslated[quote.id] ? translatedTexts[quote.id] : quote.text}"
              </blockquote>
              <p className="text-right text-lg text-indigo-600 font-semibold mb-2">- {quote.author}</p>
              {editingId === quote.id ? (
                <div>
                  <textarea
                    value={editingInsight}
                    onChange={(e) => setEditingInsight(e.target.value)}
                    className="w-full p-2 border rounded mb-2"
                    rows={3}
                  />
                  <button
                    onClick={() => handleSaveInsight(quote.id)}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mr-2"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-gray-600 italic mb-2">
                    {quote.insight ? <><span className="font-semibold">Insight:</span> {quote.insight}</> : 'No insight added yet.'}
                  </p>
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => onRemove(quote.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={20} />
                    </button>
                    <div className="space-x-2">
                      <button
                        onClick={() => handleEditClick(quote.id, quote.insight)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => setShowShareModal(quote.id)}
                        className="text-green-500 hover:text-green-700"
                      >
                        <Share2 size={20} />
                      </button>
                      <button
                        onClick={() => handleTranslate(quote)}
                        className={`${
                          isTranslated[quote.id] ? 'text-purple-500' : 'text-gray-500'
                        } hover:text-purple-700`}
                      >
                        <Languages size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showShareModal === quote.id && (
                <ShareQuote 
                  quote={quote} 
                  isTranslated={isTranslated[quote.id]}
                  translatedText={translatedTexts[quote.id]}
                  onClose={() => setShowShareModal(null)} 
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Favorites;